/**
 * Parse a site's free-text wind "direction" field into the set of 16-point
 * compass directions the site is flyable in, for the wind rose.
 *
 * Real values seen in the data:
 *   "ENE"
 *   "SE"
 *   "NE + NW"
 *   "N, NW, WNW, SE, S, SSW. 130 - 220 degs"
 *
 * Strategy: strip any trailing degree info, split on commas / plus / slashes /
 * whitespace, keep tokens that are valid compass points.
 */

export const COMPASS_16 = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
]

const COMPASS_SET = new Set(COMPASS_16)

/** Bearing (degrees, N=0) for each 16-point compass direction. */
export function bearingFor(dir) {
  const idx = COMPASS_16.indexOf(dir)
  return idx === -1 ? null : idx * 22.5
}

/**
 * @param {string} direction raw direction string
 * @returns {string[]} ordered subset of COMPASS_16 the site covers
 */
export function parseDirections(direction) {
  if (!direction || typeof direction !== 'string') return []
  // Drop anything from the first digit onward ("... 130 - 220 degs").
  const cleaned = direction.split(/[.]|(?=\d)/)[0]
  const tokens = cleaned
    .toUpperCase()
    .split(/[\s,+/&]+/)
    .map((t) => t.trim())
    .filter((t) => COMPASS_SET.has(t))
  // De-dupe, keep compass order.
  return COMPASS_16.filter((d) => tokens.includes(d))
}

/**
 * Expand a site's wind-direction string into the FULL set of 16-point compass
 * directions the site works in, expanding dash-ranges to the points in between.
 *
 * Handles the mix of formats in the data:
 *   "NE"                  -> [NE]
 *   "NE + NW", "N, NW"    -> discrete list [NE, NW] / [N, NW]
 *   "SSW - SE"            -> range SSW,S,SSE,SE (the arc <= 180 deg)
 *   "WSW - W - NW"        -> chained ranges WSW,W,WNW,NW
 *   "N, NW, WNW, SE, S, SSW. 130 - 220 degs"  -> the listed points (degrees dropped)
 *
 * A hyphen between two compass points means a range; commas/plus/slash mean a
 * discrete list. When both appear we split on the list separators first, then
 * expand any hyphenated range segments.
 */
export function expandDirections(direction) {
  if (!direction || typeof direction !== 'string') return []
  const cleaned = direction.split(/[.]|(?=\d)/)[0] // drop trailing "... degs"
  const result = new Set()

  // Split into list segments first (comma / semicolon / plus / slash / &).
  for (const seg of cleaned.toUpperCase().split(/[,;+/&]+/)) {
    // Within a segment, a hyphen chain is a range: A - B - C
    const parts = seg
      .split('-')
      .map((p) => p.replace(/[^A-Z]/g, '').trim()) // strip stray punctuation/spaces
      .filter((p) => COMPASS_SET.has(p))
    if (parts.length === 0) continue
    if (parts.length === 1) {
      result.add(parts[0])
      continue
    }
    // Expand each consecutive pair as a range along the shorter arc.
    for (let i = 0; i < parts.length - 1; i++) {
      for (const d of rangeBetween(parts[i], parts[i + 1])) result.add(d)
    }
  }
  return COMPASS_16.filter((d) => result.has(d))
}

/**
 * Normalise a free-text wind direction ("north east", "north-east") to its
 * 16-point compass abbreviation ("NE"), or return it uppercased if already one.
 */
export function normaliseDirection(input) {
  if (!input) return ''
  const s = String(input).trim().toUpperCase()
  if (COMPASS_SET.has(s)) return s
  const words = s
    .replace(/[-\s]+/g, ' ')
    .replace(/\bNORTH\b/g, 'N')
    .replace(/\bEAST\b/g, 'E')
    .replace(/\bSOUTH\b/g, 'S')
    .replace(/\bWEST\b/g, 'W')
    .replace(/\s+/g, '')
  return COMPASS_SET.has(words) ? words : s
}

/**
 * Does a site work in the given wind direction? Expands the site's direction
 * (including ranges) and checks membership. `wanted` may be an abbreviation
 * ("NE") or word form ("north east"). Empty `wanted` matches everything.
 */
export function matchesWindDirection(site, wanted) {
  const dir = normaliseDirection(wanted)
  if (!dir) return true
  return siteDirections(site).includes(dir)
}

/**
 * The compass directions a site works in: prefer the structured, admin-tagged
 * directions[] array; fall back to expanding the free-text `direction` for
 * sites not yet tagged.
 */
export function siteDirections(site) {
  if (site && Array.isArray(site.directions) && site.directions.length > 0) {
    return COMPASS_16.filter((d) => site.directions.includes(d))
  }
  return expandDirections(site && site.direction)
}

/** All compass points from A to B along the shorter arc (inclusive). */
function rangeBetween(a, b) {
  const ai = COMPASS_16.indexOf(a)
  const bi = COMPASS_16.indexOf(b)
  if (ai === -1 || bi === -1) return [a, b].filter((x) => COMPASS_SET.has(x))
  const n = COMPASS_16.length // 16
  const cw = (bi - ai + n) % n   // steps clockwise a->b
  const ccw = (ai - bi + n) % n  // steps counter-clockwise
  const out = []
  if (cw <= ccw) {
    for (let k = 0; k <= cw; k++) out.push(COMPASS_16[(ai + k) % n])
  } else {
    for (let k = 0; k <= ccw; k++) out.push(COMPASS_16[(ai - k + n) % n])
  }
  return out
}

/**
 * Given all sites, return those that belong on the wind rose: flyable sites
 * (not Closed) that parse to at least one compass direction. Each entry carries
 * its parsed directions.
 */
export function sitesForWindRose(sites) {
  if (!Array.isArray(sites)) return []
  return sites
    .filter((s) => (s.status || '').toLowerCase() !== 'closed')
    .map((s) => ({ ...s, directions: siteDirections(s) }))
    .filter((s) => s.directions.length > 0)
}
